import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  People,
  Event,
  TrendingUp,
  Schedule,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Client, Visit } from '../types';
import { clientAPI, visitAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { provider } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Dashboard: Fetching data...');
        console.log('Dashboard: Current provider:', provider);
        console.log('Dashboard: Provider ID:', provider?.provider_id);
        
        const [clientsData, visitsData] = await Promise.all([
          // Require clients for the current provider
          clientAPI.getClients(provider?.provider_id),
          // Only fetch visits for the current provider (get all visits for dashboard)
          visitAPI.getVisits(provider?.provider_id, 1000, 0), // Get up to 1000 visits for dashboard
        ]);
        
        console.log('Dashboard: Raw clients data:', clientsData);
        console.log('Dashboard: Raw visits data:', visitsData);
        console.log('Dashboard: Number of clients fetched:', clientsData.length);
        
        // Handle both array response and paginated response for visits
        let visitsArray: Visit[];
        if (Array.isArray(visitsData)) {
          visitsArray = visitsData;
        } else {
          visitsArray = (visitsData as any).items || [];
        }
        
        console.log('Dashboard: Number of visits fetched:', visitsArray.length);
        
        // Log sample client and visit data
        if (clientsData.length > 0) {
          console.log('Dashboard: Sample client:', clientsData[0]);
          console.log('Dashboard: Sample client ID type:', typeof clientsData[0].client_id);
        }
        
        if (visitsArray.length > 0) {
          console.log('Dashboard: Sample visit:', visitsArray[0]);
          console.log('Dashboard: Sample visit client_id type:', typeof visitsArray[0].client_id);
        }
        
        setClients(clientsData);
        setVisits(visitsArray);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [provider]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const totalClients = clients.length;
  const totalVisits = visits.length;
  const completedVisits = visits.filter(visit => visit.status === 'completed').length;
  const upcomingVisits = visits.filter(visit => visit.status === 'scheduled').length;

  const recentVisits = visits
    .filter(visit => visit.scheduled_start) // Filter out visits without scheduled start times
    .sort((a, b) => {
      const dateA = new Date(a.scheduled_start || '');
      const dateB = new Date(b.scheduled_start || '');
      // Check if dates are valid before comparing
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        return 0;
      }
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, 5);

  const stats = [
    {
      title: 'Total Clients',
      value: totalClients,
      icon: <People sx={{ fontSize: 40, color: '#2D4B73' }} />,
      color: '#2D4B73',
    },
    {
      title: 'Total Visits',
      value: totalVisits,
      icon: <Event sx={{ fontSize: 40, color: '#2D4B73' }} />,
      color: '#2D4B73',
    },
    {
      title: 'Completed Visits',
      value: completedVisits,
      icon: <TrendingUp sx={{ fontSize: 40, color: '#059669' }} />,
      color: '#059669',
    },
    {
      title: 'Upcoming Visits',
      value: upcomingVisits,
      icon: <Schedule sx={{ fontSize: 40, color: '#d97706' }} />,
      color: '#d97706',
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'primary';
      case 'cancelled':
        return 'error';
      case 'no-show':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#2D4B73' }}>
        Provider Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Card sx={{ 
              borderRadius: 16,
              backgroundColor: '#ffffff',
              border: '1px solid rgba(45, 75, 115, 0.2)',
              boxShadow: '0 8px 32px 0 rgba(45, 75, 115, 0.15)',
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: stat.color }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3, 
            borderRadius: 16,
            backgroundColor: '#ffffff',
            border: '1px solid rgba(45, 75, 115, 0.2)',
            boxShadow: '0 8px 32px 0 rgba(45, 75, 115, 0.15)',
          }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2D4B73' }}>
              Recent Visits
            </Typography>
            <List>
              {recentVisits.length > 0 ? (
                recentVisits.map((visit) => (
                  <ListItem key={visit.visit_id} sx={{ 
                    borderRadius: 8, 
                    mb: 1,
                    backgroundColor: '#ffffff',
                  }}>
                    <ListItemIcon>
                      <Event sx={{ color: '#2D4B73' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={(() => {
                        console.log('Dashboard: Visit client data:', visit.client);
                        console.log('Dashboard: Visit clientId:', visit.client_id);
                        
                        if (visit.client) {
                          return `${visit.client.first_name || ''} ${visit.client.last_name || ''}`.trim() || 'Unknown Client';
                        } else {
                          // Try to find client by clientId if client data is not populated
                          const visitClientId = visit.client_id;
                          console.log('Dashboard: Looking for client with ID:', visitClientId);
                          console.log('Dashboard: Available client IDs:', clients.map(c => c.client_id));
                          
                          const foundClient = clients.find(c => {
                            const clientId = c.client_id;
                            console.log('Dashboard: Comparing visit clientId:', visitClientId, 'with client ID:', clientId);
                            return clientId.toString() === visitClientId?.toString();
                          });
                          
                          if (foundClient) {
                            console.log('Dashboard: Found client:', foundClient);
                            return `${foundClient.first_name} ${foundClient.last_name}`;
                          }
                          
                          console.log('Dashboard: No client found for ID:', visitClientId);
                          return 'Client not found';
                        }
                      })()}
                      secondary={(() => {
                        try {
                          const dateString = visit.scheduled_start || '';
                          if (!dateString) return 'Not scheduled';
                          
                          // Parse date directly to avoid timezone conversion
                          const datePart = dateString.split('T')[0];
                          const [year, month, day] = datePart.split('-').map(Number);
                          
                          if (isNaN(year) || isNaN(month) || isNaN(day)) {
                            // Fallback to Date parsing
                            const date = new Date(dateString);
                            if (isNaN(date.getTime())) {
                              return 'Not scheduled';
                            }
                            return format(date, 'MMM dd, yyyy h:mm a');
                          }
                          
                          // Parse time part if available
                          let hours = 0, minutes = 0;
                          if (dateString.includes('T')) {
                            const timeStr = dateString.split('T')[1];
                            if (timeStr) {
                              // Extract hours and minutes (handle formats like "HH:mm:ss" or "HH:mm:ss.sssZ")
                              const timeMatch = timeStr.match(/(\d{2}):(\d{2})/);
                              if (timeMatch) {
                                hours = parseInt(timeMatch[1], 10);
                                minutes = parseInt(timeMatch[2], 10);
                              }
                            }
                          }
                          
                          // Create date object using local timezone to avoid conversion issues
                          const dateObj = new Date(year, month - 1, day, hours, minutes);
                          return format(dateObj, 'MMM dd, yyyy h:mm a');
                        } catch (error) {
                          return 'Invalid date';
                        }
                      })()}
                      sx={{ 
                        '& .MuiListItemText-primary': { fontWeight: 600, color: '#2D4B73' },
                        '& .MuiListItemText-secondary': { color: '#2D4B73' }
                      }}
                    />
                    <Chip 
                      label={visit.status || 'Unknown'} 
                      color={getStatusColor(visit.status || '') as any}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText 
                    primary="No recent visits" 
                    sx={{ 
                      '& .MuiListItemText-primary': { color: '#2D4B73', fontStyle: 'italic' }
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3, 
            borderRadius: 16,
            backgroundColor: '#ffffff',
            border: '1px solid rgba(45, 75, 115, 0.2)',
            boxShadow: '0 8px 32px 0 rgba(45, 75, 115, 0.15)',
          }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2D4B73' }}>
              Quick Actions
            </Typography>
            <List>
              <ListItem 
                button 
                onClick={() => navigate('/visits')}
                sx={{ 
                  borderRadius: 8, 
                  mb: 1,
                  backgroundColor: '#ffffff',
                  '&:hover': {
                    backgroundColor: 'rgba(45, 75, 115, 0.1)',
                  },
                  cursor: 'pointer'
                }}
              >
                <ListItemIcon>
                  <Event sx={{ color: '#2D4B73' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Create New Visit" 
                  sx={{ 
                    '& .MuiListItemText-primary': { fontWeight: 600, color: '#2D4B73' }
                  }}
                />
              </ListItem>
              <ListItem 
                button 
                onClick={() => navigate('/clients')}
                sx={{ 
                  borderRadius: 8,
                  backgroundColor: '#ffffff',
                  '&:hover': {
                    backgroundColor: 'rgba(45, 75, 115, 0.1)',
                  },
                  cursor: 'pointer'
                }}
              >
                <ListItemIcon>
                  <People sx={{ color: '#2D4B73' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="View All Clients" 
                  sx={{ 
                    '& .MuiListItemText-primary': { fontWeight: 600, color: '#2D4B73' }
                  }}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
